import { useEffect, useState } from 'react';
import { storeService } from '../services/storeService';
import { StoreCategory, StoreCatalogItem } from '../models';

const PRODUCTS_PER_GENDER = 3;
const PRODUCTS_PER_CATEGORY = 6;
const GENDER_MASCULINO = 1;
const GENDER_FEMININO = 2;

export type CategoryShowcaseGender = 'girl' | 'boy';

export interface CategoryShowcaseProduct extends StoreCatalogItem {
  showcaseGender: CategoryShowcaseGender;
}

export interface CategoryShowcaseRow {
  category: StoreCategory;
  products: CategoryShowcaseProduct[];
}

function orderByGender(girls: StoreCatalogItem[], boys: StoreCatalogItem[]): CategoryShowcaseProduct[] {
  return [
    ...girls.map((item) => ({ ...item, showcaseGender: 'girl' as const })),
    ...boys.map((item) => ({ ...item, showcaseGender: 'boy' as const })),
  ];
}

async function fetchCategoryRow(category: StoreCategory): Promise<CategoryShowcaseRow> {
  const [boysResult, girlsResult] = await Promise.all([
    storeService.getCatalog({
      pageNumber: 1,
      pageSize: PRODUCTS_PER_GENDER,
      categoryIds: [category.id],
      gender: GENDER_MASCULINO,
      sortBy: 'arrival',
      sortDirection: 'desc',
    }),
    storeService.getCatalog({
      pageNumber: 1,
      pageSize: PRODUCTS_PER_GENDER,
      categoryIds: [category.id],
      gender: GENDER_FEMININO,
      sortBy: 'arrival',
      sortDirection: 'desc',
    }),
  ]);

  let boys = boysResult.items;
  let girls = girlsResult.items;

  const total = boys.length + girls.length;
  if (total < PRODUCTS_PER_CATEGORY) {
    const deficit = PRODUCTS_PER_CATEGORY - total;

    if (girls.length === PRODUCTS_PER_GENDER && boys.length < PRODUCTS_PER_GENDER) {
      const expandedGirls = await storeService.getCatalog({
        pageNumber: 1,
        pageSize: PRODUCTS_PER_GENDER + deficit,
        categoryIds: [category.id],
        gender: GENDER_FEMININO,
        sortBy: 'arrival',
        sortDirection: 'desc',
      });
      girls = expandedGirls.items;
    } else if (boys.length === PRODUCTS_PER_GENDER && girls.length < PRODUCTS_PER_GENDER) {
      const expandedBoys = await storeService.getCatalog({
        pageNumber: 1,
        pageSize: PRODUCTS_PER_GENDER + deficit,
        categoryIds: [category.id],
        gender: GENDER_MASCULINO,
        sortBy: 'arrival',
        sortDirection: 'desc',
      });
      boys = expandedBoys.items;
    }
  }

  return { category, products: orderByGender(girls, boys) };
}

export function useCategoryShowcaseProducts(categories: StoreCategory[]) {
  const [rows, setRows] = useState<CategoryShowcaseRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (categories.length === 0) {
      setRows([]);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);

    Promise.all(categories.map((category) => fetchCategoryRow(category)))
      .then((results) => {
        if (active) setRows(results.filter((row) => row.products.length > 0));
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err : new Error('Erro ao carregar produtos das categorias'));
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [categories]);

  return { rows, isLoading, error, isEmpty: rows.length === 0 };
}
